import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface LearningFilters {
  userType: "all" | "mentor" | "entrepreneur";
  curriculumId: string | null;
  courseId: string | null;
  moduleId: string | null;
  cohortId: string | null;
}


export interface ActiveUser {
  userId: string;
  fullName: string;
  avatarUrl: string | null;
  role: string;
  modulesCompleted: number;
  quizzesPassed: number;
  assignmentsSubmitted: number;
  discussionsCreated: number;
  discussionReplies: number;
  activityScore: number;
}

export interface LearningAnalyticsData {
  totalLearners: number;
  curriculumEnrollments: { total: number; completed: number };
  courseEnrollments: { total: number; completed: number };
  modulesProgress: { total: number; completed: number };
  quizzes: { total: number; passed: number; avgScore: number };
  // `total` was previously returned equal to `submitted`, so no ratio was ever
  // possible. No consumer read it, so it is dropped rather than faked.
  assignments: { submitted: number };
  discussions: { created: number; replies: number };
  helpRequests: { total: number; resolved: number };
  certificatesIssued: number;
  mostActiveUsers: ActiveUser[];
}

export function useLearningAnalytics(filters: LearningFilters) {
  return useQuery({
    queryKey: ["learning_analytics", filters],
    queryFn: async (): Promise<LearningAnalyticsData> => {
      // Get user IDs filtered by role if needed
      let userIds: string[] | null = null;
      
      if (filters.userType !== "all") {
        const { data: profiles } = await supabase
          .from("profiles")
          .select("id")
          .eq("role", filters.userType);
        userIds = profiles?.map((p) => p.id) || [];
      }

      // If cohort filter is set, restrict to cohort members (intersect with userType)
      if (filters.cohortId) {
        const { data: cohortEnrollments } = await supabase
          .from("course_enrollments")
          .select("user_id")
          .eq("cohort_id", filters.cohortId);
        const cohortUserIds = [...new Set((cohortEnrollments || []).map((e) => e.user_id))];
        userIds = userIds
          ? userIds.filter((id) => cohortUserIds.includes(id))
          : cohortUserIds;
      }


      // Get curriculum enrollments
      let curriculumQuery = supabase.from("curriculum_enrollments").select("id, user_id, completed_at, curriculum_id");
      if (userIds) {
        curriculumQuery = curriculumQuery.in("user_id", userIds);
      }
      if (filters.curriculumId) {
        curriculumQuery = curriculumQuery.eq("curriculum_id", filters.curriculumId);
      }
      const { data: curriculumEnrollments } = await curriculumQuery;

      // Get course IDs for the selected curriculum if applicable
      let courseIdsInCurriculum: string[] | null = null;
      if (filters.curriculumId) {
        const { data: curriculumCourses } = await supabase
          .from("curriculum_courses")
          .select("course_id")
          .eq("curriculum_id", filters.curriculumId);
        courseIdsInCurriculum = curriculumCourses?.map((c) => c.course_id) || [];
      }

      // Get course enrollments
      let courseQuery = supabase.from("course_enrollments").select("id, user_id, completed_at, course_id");
      if (userIds) {
        courseQuery = courseQuery.in("user_id", userIds);
      }
      if (filters.courseId) {
        courseQuery = courseQuery.eq("course_id", filters.courseId);
      } else if (courseIdsInCurriculum) {
        // An empty derived list must mean "match nothing", not "no filter".
        // Previously a curriculum with no courses fell through to UNFILTERED
        // global counts. Sentinel mirrors the pattern in useCohorts.ts.
        courseQuery = courseQuery.in(
          "course_id",
          courseIdsInCurriculum.length > 0 ? courseIdsInCurriculum : ["00000000-0000-0000-0000-000000000000"]
        );
      }
      const { data: courseEnrollments } = await courseQuery;
      const enrollmentIds = courseEnrollments?.map((e) => e.id) || [];

      // Get module IDs for the selected course if applicable
      let moduleIdsInCourse: string[] | null = null;
      if (filters.courseId) {
        const { data: modules } = await supabase
          .from("modules")
          .select("id")
          .eq("course_id", filters.courseId);
        moduleIdsInCourse = modules?.map((m) => m.id) || [];
      }

      // Get module progress
      let moduleQuery = supabase.from("module_progress").select("id, enrollment_id, completed_at, module_id");
      // Always filter. An empty enrollmentIds list previously meant "no filter",
      // so filtering by a cohort whose members have no enrollments reported
      // platform-wide totals instead of zero.
      moduleQuery = moduleQuery.in(
        "enrollment_id",
        enrollmentIds.length > 0 ? enrollmentIds : ["00000000-0000-0000-0000-000000000000"]
      );
      if (filters.moduleId) {
        moduleQuery = moduleQuery.eq("module_id", filters.moduleId);
      } else if (moduleIdsInCourse) {
        moduleQuery = moduleQuery.in(
          "module_id",
          moduleIdsInCourse.length > 0 ? moduleIdsInCourse : ["00000000-0000-0000-0000-000000000000"]
        );
      }
      const { data: moduleProgress } = await moduleQuery;

      // Get quiz attempts
      let quizQuery = supabase.from("quiz_attempts").select("id, user_id, passed, percentage, quiz_id");
      if (userIds) {
        quizQuery = quizQuery.in("user_id", userIds);
      }
      const { data: quizAttempts } = await quizQuery;

      // Filter quiz attempts by module if needed
      let filteredQuizAttempts = quizAttempts || [];
      if (filters.moduleId || moduleIdsInCourse) {
        const targetModuleIds = filters.moduleId ? [filters.moduleId] : moduleIdsInCourse;
        if (targetModuleIds && targetModuleIds.length > 0) {
          const { data: quizzes } = await supabase
            .from("quizzes")
            .select("id")
            .in("module_id", targetModuleIds);
          const quizIds = quizzes?.map((q) => q.id) || [];
          filteredQuizAttempts = filteredQuizAttempts.filter((a) => quizIds.includes(a.quiz_id));
        } else {
          // Scoped to a course/module set that contains nothing -> no attempts.
          filteredQuizAttempts = [];
        }
      }

      // Get assignment submissions
      let assignmentQuery = supabase.from("assignment_submissions").select("id, user_id, assignment_id");
      if (userIds) {
        assignmentQuery = assignmentQuery.in("user_id", userIds);
      }
      const { data: assignmentSubmissions } = await assignmentQuery;

      // Filter assignments by module if needed
      let filteredAssignments = assignmentSubmissions || [];
      if (filters.moduleId || moduleIdsInCourse) {
        const targetModuleIds = filters.moduleId ? [filters.moduleId] : moduleIdsInCourse;
        if (targetModuleIds && targetModuleIds.length > 0) {
          const { data: assignments } = await supabase
            .from("assignments")
            .select("id")
            .in("module_id", targetModuleIds);
          const assignmentIds = assignments?.map((a) => a.id) || [];
          filteredAssignments = filteredAssignments.filter((s) => assignmentIds.includes(s.assignment_id));
        } else {
          // Scoped to a course/module set that contains nothing -> no submissions.
          filteredAssignments = [];
        }
      }

      // Get discussions (course discussions)
      let discussionsQuery = supabase.from("course_discussions").select("id, user_id");
      if (userIds) {
        discussionsQuery = discussionsQuery.in("user_id", userIds);
      }
      if (filters.courseId) {
        discussionsQuery = discussionsQuery.eq("course_id", filters.courseId);
      }
      const { data: courseDiscussions } = await discussionsQuery;

      // Get general discussions
      let generalDiscussionsQuery = supabase.from("general_discussions").select("id, user_id");
      if (userIds) {
        generalDiscussionsQuery = generalDiscussionsQuery.in("user_id", userIds);
      }
      const { data: generalDiscussions } = await generalDiscussionsQuery;

      // Get mentor discussions
      let mentorDiscussionsQuery = supabase.from("mentor_discussions").select("id, user_id");
      if (userIds) {
        mentorDiscussionsQuery = mentorDiscussionsQuery.in("user_id", userIds);
      }
      const { data: mentorDiscussions } = await mentorDiscussionsQuery;

      // Get discussion replies
      let discussionRepliesQuery = supabase.from("discussion_replies").select("id, user_id");
      if (userIds) {
        discussionRepliesQuery = discussionRepliesQuery.in("user_id", userIds);
      }
      const { data: discussionReplies } = await discussionRepliesQuery;

      let generalRepliesQuery = supabase.from("general_discussion_replies").select("id, user_id");
      if (userIds) {
        generalRepliesQuery = generalRepliesQuery.in("user_id", userIds);
      }
      const { data: generalReplies } = await generalRepliesQuery;

      let mentorRepliesQuery = supabase.from("mentor_discussion_replies").select("id, user_id");
      if (userIds) {
        mentorRepliesQuery = mentorRepliesQuery.in("user_id", userIds);
      }
      const { data: mentorReplies } = await mentorRepliesQuery;

      // Get help requests
      let helpQuery = supabase.from("help_requests").select("id, user_id, status, course_id");
      if (userIds) {
        helpQuery = helpQuery.in("user_id", userIds);
      }
      if (filters.courseId) {
        helpQuery = helpQuery.eq("course_id", filters.courseId);
      }
      const { data: helpRequests } = await helpQuery;

      // Get certificates
      let certQuery = supabase.from("certificates").select("id, user_id, curriculum_id");
      if (userIds) {
        certQuery = certQuery.in("user_id", userIds);
      }
      if (filters.curriculumId) {
        certQuery = certQuery.eq("curriculum_id", filters.curriculumId);
      }
      const { data: certificates } = await certQuery;

      // Calculate unique learners (users enrolled in any curriculum or course)
      const learnerIds = new Set<string>();
      curriculumEnrollments?.forEach((e) => learnerIds.add(e.user_id));
      courseEnrollments?.forEach((e) => learnerIds.add(e.user_id));

      // Calculate quiz stats
      const avgQuizScore =
        filteredQuizAttempts.length > 0
          ? filteredQuizAttempts.reduce((sum, a) => sum + (a.percentage || 0), 0) / filteredQuizAttempts.length
          : 0;

      const totalDiscussions =
        (courseDiscussions?.length || 0) +
        (generalDiscussions?.length || 0) +
        (mentorDiscussions?.length || 0);

      const totalReplies =
        (discussionReplies?.length || 0) +
        (generalReplies?.length || 0) +
        (mentorReplies?.length || 0);

      // Calculate most active users
      const userActivity = new Map<string, {
        modulesCompleted: number;
        quizzesPassed: number;
        assignmentsSubmitted: number;
        discussionsCreated: number;
        discussionReplies: number;
      }>();

      // Initialize activity for all learners
      learnerIds.forEach((userId) => {
        userActivity.set(userId, {
          modulesCompleted: 0,
          quizzesPassed: 0,
          assignmentsSubmitted: 0,
          discussionsCreated: 0,
          discussionReplies: 0,
        });
      });

      // Count modules completed per user (need to map enrollment to user)
      const enrollmentToUser = new Map<string, string>();
      courseEnrollments?.forEach((e) => enrollmentToUser.set(e.id, e.user_id));
      
      moduleProgress?.forEach((mp) => {
        if (mp.completed_at) {
          const userId = enrollmentToUser.get(mp.enrollment_id);
          if (userId && userActivity.has(userId)) {
            userActivity.get(userId)!.modulesCompleted++;
          }
        }
      });

      // Count quizzes passed per user
      filteredQuizAttempts.forEach((qa) => {
        if (qa.passed && userActivity.has(qa.user_id)) {
          userActivity.get(qa.user_id)!.quizzesPassed++;
        }
      });

      // Count assignments submitted per user
      filteredAssignments.forEach((as) => {
        if (userActivity.has(as.user_id)) {
          userActivity.get(as.user_id)!.assignmentsSubmitted++;
        }
      });

      // Count discussions created per user
      [...(courseDiscussions || []), ...(generalDiscussions || []), ...(mentorDiscussions || [])].forEach((d) => {
        if (userActivity.has(d.user_id)) {
          userActivity.get(d.user_id)!.discussionsCreated++;
        }
      });

      // Count discussion replies per user
      [...(discussionReplies || []), ...(generalReplies || []), ...(mentorReplies || [])].forEach((r) => {
        if (userActivity.has(r.user_id)) {
          userActivity.get(r.user_id)!.discussionReplies++;
        }
      });

      // Get user profiles for the top active users
      const userActivityArray = Array.from(userActivity.entries()).map(([userId, activity]) => {
        const activityScore =
          activity.modulesCompleted * 5 +
          activity.quizzesPassed * 3 +
          activity.assignmentsSubmitted * 4 +
          activity.discussionsCreated * 2 +
          activity.discussionReplies * 1;
        return { userId, ...activity, activityScore };
      });

      // Sort by activity score and take top 10
      userActivityArray.sort((a, b) => b.activityScore - a.activityScore);
      const topUserIds = userActivityArray.slice(0, 10).map((u) => u.userId);

      let mostActiveUsers: ActiveUser[] = [];
      if (topUserIds.length > 0) {
        const { data: profiles } = await supabase
          .from("profiles")
          .select("id, full_name, avatar_url, role")
          .in("id", topUserIds);

        mostActiveUsers = userActivityArray.slice(0, 10).map((activity) => {
          const profile = profiles?.find((p) => p.id === activity.userId);
          return {
            userId: activity.userId,
            fullName: profile?.full_name || "Unknown",
            avatarUrl: profile?.avatar_url || null,
            role: profile?.role || "unknown",
            modulesCompleted: activity.modulesCompleted,
            quizzesPassed: activity.quizzesPassed,
            assignmentsSubmitted: activity.assignmentsSubmitted,
            discussionsCreated: activity.discussionsCreated,
            discussionReplies: activity.discussionReplies,
            activityScore: activity.activityScore,
          };
        });
      }

      return {
        totalLearners: learnerIds.size,
        curriculumEnrollments: {
          total: curriculumEnrollments?.length || 0,
          completed: curriculumEnrollments?.filter((e) => e.completed_at).length || 0,
        },
        courseEnrollments: {
          total: courseEnrollments?.length || 0,
          completed: courseEnrollments?.filter((e) => e.completed_at).length || 0,
        },
        modulesProgress: {
          total: moduleProgress?.length || 0,
          completed: moduleProgress?.filter((m) => m.completed_at).length || 0,
        },
        quizzes: {
          total: filteredQuizAttempts.length,
          passed: filteredQuizAttempts.filter((a) => a.passed).length,
          avgScore: Math.round(avgQuizScore),
        },
        assignments: {
          submitted: filteredAssignments.length,
        },
        discussions: {
          created: totalDiscussions,
          replies: totalReplies,
        },
        helpRequests: {
          total: helpRequests?.length || 0,
          resolved: helpRequests?.filter((h) => h.status === "resolved").length || 0,
        },
        certificatesIssued: certificates?.length || 0,
        mostActiveUsers,
      };
    },
  });
}

// Hook to fetch filter options
export function useLearningFilterOptions() {
  const { data: curricula } = useQuery({
    queryKey: ["curricula_options"],
    queryFn: async () => {
      const { data } = await supabase.from("curricula").select("id, title").order("title");
      return data || [];
    },
  });

  const { data: courses } = useQuery({
    queryKey: ["courses_options"],
    queryFn: async () => {
      const { data } = await supabase
        .from("courses")
        .select("id, title, curriculum_id")
        .order("title");
      return data || [];
    },
  });

  const { data: modules } = useQuery({
    queryKey: ["modules_options"],
    queryFn: async () => {
      const { data } = await supabase
        .from("modules")
        .select("id, title, course_id, sequence_order")
        .order("sequence_order");
      return data || [];
    },
  });

  const { data: cohorts } = useQuery({
    queryKey: ["cohorts_options"],
    queryFn: async () => {
      const { data } = await supabase
        .from("cohorts")
        .select("id, name, is_active")
        .order("created_at", { ascending: false });
      return data || [];
    },
  });

  return { curricula, courses, modules, cohorts };
}

